import { useEffect, useState } from "react";

import { api } from "../../api/client";
import { AlertBanner } from "../feedback/AlertBanner";
import { ResponsiveTable } from '../ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import { formatDate, formatDateTime, PROGRAM_STATUS_LABELS, SEX_LABELS, ANIMAL_STATUS_LABELS } from "../../lib/format";
import { EarTag } from "../tags/EarTag";
import { useModalHistoryEntry } from "../../lib/modalHistory";
import type { IrzEvent, IrzLocalDetail, IrzLocation, Animal } from "../../types";

type Props = {
  tag: string | null;
  animals: Animal[];
  onClose: () => void;
  onOpenTag: (tag: string) => void;
  /** Nie rejestruj w historii wstecz (gdy otwarte nad innym modalem). */
  skipHistory?: boolean;
};

function display(value: unknown): string {
  if (value == null || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Tak" : "Nie";
  }
  return String(value);
}

function sexLabel(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return SEX_LABELS[value] || value;
}

function formatLocation(location: IrzLocation | null | undefined): string {
  if (!location) {
    return "—";
  }
  const parts = [location.date, location.voivodeship, location.county].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

function formatEventDetails(details: IrzEvent["details"]): string {
  if (!details || typeof details !== "object") {
    return "—";
  }
  const parts = Object.entries(details)
    .filter(([, value]) => value != null && value !== "")
    .map(([key, value]) => `${key}: ${String(value)}`);
  return parts.length ? parts.join("; ") : "—";
}

function Fact({ label, value, tag = false }: { label: string; value: unknown; tag?: boolean }) {
  const text = display(value);
  return (
    <div className="irz-detail__fact">
      <dt>{label}</dt>
      <dd>{tag && text !== "—" ? <EarTag value={text} /> : text}</dd>
    </div>
  );
}

export function AnimalDetailModal({
  tag,
  animals,
  onClose,
  onOpenTag,
  skipHistory = false,
}: Props) {
  const [profile, setProfile] = useState<IrzLocalDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const local = animals.find((item) => item.tag === tag) ?? null;
  useModalHistoryEntry(skipHistory ? false : Boolean(tag), onClose);

  useEffect(() => {
    if (!tag) {
      setProfile(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProfile(null);
    void (async () => {
      const result = await api.GET("/api/irz/local-animals/{tag}", {
        params: { path: { tag } },
      });
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (result.error) {
        setError(apiError(result.error, "Nie udało się wczytać szczegółów zwierzęcia."));
        return;
      }
      const data = result.data as IrzLocalDetail;
      setProfile(data);
      if (
        data.source === "empty" &&
        !(data.events && data.events.length) &&
        !(data.movements && data.movements.length)
      ) {
        setError(
          "Podstawowe dane lokalne. Pełną historię IRZ pobierzesz na stronie IRZ („Pobierz szczegóły”).",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tag]);

  useEffect(() => {
    if (!tag) {
      return;
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, tag]);

  if (!tag) {
    return null;
  }

  const details = profile?.details ?? {};
  const children = (
    profile?.children?.length
      ? profile.children
      : animals.filter((item) => item.mother_tag === tag)
  ).map((child) => ({
    tag: child.tag,
    sex: child.sex,
    birth_date: child.birth_date,
    status: "irz_status" in child && child.irz_status ? child.irz_status : child.status,
  }));
  const means = details.identification_means ?? [];
  const events = profile?.events ?? [];
  const movements = profile?.movements ?? [];

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal modal--xl irz-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="animal-detail-title"
        data-testid="animal-detail-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="irz-detail-modal__toolbar">
          <div className="irz-detail-modal__toolbar-text">
            <h3 className="modal__title" id="animal-detail-title">
              Szczegóły zwierzęcia
            </h3>
            <p className="modal__text modal__text--sm">
              <EarTag value={tag} />
            </p>
          </div>
          <button className="modal-close-btn" type="button" aria-label="Zamknij szczegóły" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="irz-detail-modal__body">
          {loading ? <p className="muted">Wczytywanie szczegółów…</p> : null}
          <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
          <dl className="irz-detail__grid">
            <Fact label="Kolczyk" tag value={details.tag || local?.tag || tag} />
            <Fact label="Płeć" value={sexLabel(local?.sex || details.sex)} />
            <Fact label="Data urodzenia" value={formatDate(details.birth_date || local?.birth_date)} />
            <Fact label="Data przybycia" value={formatDate(details.arrival_date || local?.arrival_date)} />
            <Fact label="Rasa" value={details.breed || local?.breed} />
            <Fact label="Gatunek" value={details.species} />
            <Fact
              label="Program"
              value={
                local?.program_status
                  ? PROGRAM_STATUS_LABELS[local.program_status] || local.program_status
                  : null
              }
            />
            <Fact
              label="Status"
              value={local?.status ? ANIMAL_STATUS_LABELS[local.status] || local.status : details.status}
            />
            <Fact label="Status IRZ" value={details.status} />
            <Fact label="Status epizootyczny" value={details.epizootic_status} />
            <Fact label="Typ użytkowy" value={details.utility_type} />
            <Fact label="Kierunek użytkowania" value={details.usage_direction} />
            <Fact label="Działalność" value={details.activity_number} />
            <Fact label="Matka" tag value={details.mother_tag || local?.mother_tag} />
            <Fact label="Ojciec / dawca" tag value={details.father_tag} />
            <Fact label="Dawczyni" tag value={details.donor_tag} />
            <Fact label="Poprzedni numer" tag value={details.previous_tag} />
            <Fact label="Czy matka" value={details.is_mother} />
            <Fact label="Sposób oznakowania" value={details.marking_method} />
            <Fact label="Masa ciała" value={details.body_weight} />
            <Fact label="Masa tuszy" value={details.carcass_weight} />
            <Fact label="Oświadczenie DDS" value={details.dds_declaration} />
            <Fact label="Ostatni sync listy IRZ" value={formatDateTime(profile?.irz_synced_at)} />
            <Fact
              label="Ostatni sync szczegółów IRZ"
              value={formatDateTime(profile?.irz_details_synced_at)}
            />
          </dl>

          <section className="irz-detail__section">
            <h4 className="irz-detail__title">Środki identyfikacji</h4>
            <div className="table-wrap">
              <ResponsiveTable className="tbl">
                <thead>
                  <tr>
                    <th>Typ</th>
                    <th>Lokalizacja</th>
                    <th>Duplikat</th>
                    <th>Ważny od</th>
                  </tr>
                </thead>
                <tbody>
                  {means.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="muted">
                        Brak środków identyfikacji.
                      </td>
                    </tr>
                  ) : (
                    means.map((item, index) => (
                      <tr key={`${item.type || "mean"}-${index}`}>
                        <td>{display(item.type)}</td>
                        <td>{display(item.transponder_location)}</td>
                        <td>{display(item.duplicate_number)}</td>
                        <td>{display(item.valid_from)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ResponsiveTable>
            </div>
          </section>

          <section className="irz-detail__section">
            <h4 className="irz-detail__title">Potomstwo</h4>
            <div className="table-wrap">
              <ResponsiveTable className="tbl">
                <thead>
                  <tr>
                    <th>Kolczyk</th>
                    <th>Płeć</th>
                    <th>Urodzenie</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {children.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="muted">
                        Brak potomstwa w lokalnej bazie.
                      </td>
                    </tr>
                  ) : (
                    children.map((child) => (
                      <tr key={child.tag}>
                        <td>
                          <button
                            type="button"
                            className="irz-tag-link"
                            onClick={() => onOpenTag(child.tag)}
                          >
                            <EarTag value={child.tag} />
                          </button>
                        </td>
                        <td>{sexLabel(child.sex)}</td>
                        <td>{formatDate(child.birth_date)}</td>
                        <td>{display(child.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ResponsiveTable>
            </div>
          </section>

          <section className="irz-detail__section">
            <h4 className="irz-detail__title">Zdarzenia IRZ</h4>
            <div className="table-wrap">
              <ResponsiveTable className="tbl">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Typ</th>
                    <th>Stan</th>
                    <th>Działalność</th>
                    <th>Szczegóły</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="muted">
                        {profile?.source === "empty"
                          ? "Brak zdarzeń IRZ — zsynchronizuj szczegóły na stronie IRZ."
                          : "Brak zdarzeń IRZ."}
                      </td>
                    </tr>
                  ) : (
                    events.map((event, index) => (
                      <tr key={`${event.event_date || "ev"}-${index}`}>
                        <td>{display(event.event_date)}</td>
                        <td>{display(event.event_type)}</td>
                        <td>{display(event.event_state)}</td>
                        <td>{display(event.reporting_activity)}</td>
                        <td>{formatEventDetails(event.details)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ResponsiveTable>
            </div>
          </section>

          <section className="irz-detail__section">
            <h4 className="irz-detail__title">Przemieszczenia</h4>
            <div className="table-wrap">
              <ResponsiveTable className="tbl">
                <thead>
                  <tr>
                    <th>Lp.</th>
                    <th>Przybycie</th>
                    <th>Wyjście</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="muted">
                        {profile?.source === "empty"
                          ? "Brak przemieszczeń — zsynchronizuj szczegóły na stronie IRZ."
                          : "Brak przemieszczeń IRZ."}
                      </td>
                    </tr>
                  ) : (
                    movements.map((move, index) => (
                      <tr key={`${move.lp ?? index}`}>
                        <td>{display(move.lp ?? index + 1)}</td>
                        <td>{formatLocation(move.arrival)}</td>
                        <td>{formatLocation(move.departure)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ResponsiveTable>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
